/**
 * Approval inbox API client.
 *
 * Shape mirrors `os_core/api/approvals.py`. We don't try to replicate the
 * server's union types — UI consumers should look at `status` and the
 * `events` array (for the detail view) and render accordingly.
 */

import { api, type RequestOptions } from "./client";


export interface ApprovalDto {
  id:                 string;
  correlation_id:     string | null;
  action:             string;
  proposed_by_agent:  string;
  target_type:        string | null;
  target_id:          string | null;
  payload:            Record<string, unknown>;
  reasoning:          string;
  confidence:         number;
  impact_estimate:    Record<string, unknown>;
  rollback_plan:      string | null;
  status:             "pending" | "approved" | "rejected" | "expired" | "executed" | "failed" | "rolled_back" | "cancelled";
  decided_by:         string | null;
  decided_at:         string | null;
  decision_note:      string | null;
  executed_at:        string | null;
  execution_result:   Record<string, unknown> | null;
  chain_id:           string | null;
  chain_order:        number | null;
  requires_after:     string | null;
  viewed_at:          string | null;
  expires_at:         string;
  // Sprint AA — escalation.
  escalate_after_s:   number | null;
  escalated_at:       string | null;
  escalation_count:   number;
  created_at:         string;
  updated_at:         string;
}


export interface ApprovalEventDto {
  id:           string;
  event_type:   string;
  actor_type:   string | null;
  actor_id:     string | null;
  note:         string | null;
  details:      Record<string, unknown>;
  occurred_at:  string;
}


export interface ApprovalDetailDto extends ApprovalDto {
  events: ApprovalEventDto[];
}


export interface DecisionRequest {
  note?:             string;
  modified_payload?: Record<string, unknown>;
}


export const approvalsApi = {
  list(params: {
    status?: ApprovalDto["status"];
    action?: string;
    limit?:  number;
  } = {}, opts?: RequestOptions): Promise<ApprovalDto[]> {
    const q = new URLSearchParams();
    if (params.status) q.set("status", params.status);
    if (params.action) q.set("action", params.action);
    q.set("limit", String(params.limit ?? 50));
    return api.get<ApprovalDto[]>(`/approvals?${q.toString()}`, opts);
  },

  get(id: string, opts?: RequestOptions): Promise<ApprovalDetailDto> {
    return api.get<ApprovalDetailDto>(`/approvals/${id}`, opts);
  },

  view(id: string, opts?: RequestOptions): Promise<void> {
    return api.post<void>(`/approvals/${id}/view`, undefined, opts);
  },

  approve(id: string, body: DecisionRequest = {}, opts?: RequestOptions): Promise<ApprovalDto> {
    return api.post<ApprovalDto>(`/approvals/${id}/approve`, body, opts);
  },

  reject(id: string, body: DecisionRequest = {}, opts?: RequestOptions): Promise<ApprovalDto> {
    return api.post<ApprovalDto>(`/approvals/${id}/reject`, body, opts);
  },
};
